import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  Stack,
  Text,
  useToast,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';

/**
 * Login page component
 * Handles user authentication with email and password
 */
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get the redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '/';

  // React Hook Form setup
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm();

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Call login function from auth context
      const user = await login(data.email, data.password);
      
      // Show success message
      toast({
        title: 'تم تسجيل الدخول بنجاح',
        description: `مرحباً بك ${user.full_name || 'في نظام مجالسنا'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      
      // Redirect based on user role
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'committee') {
        navigate('/committee/dashboard');
      } else {
        navigate(from);
      }
    } catch (error) {
      // Show error message
      let errorMessage = 'حدث خطأ أثناء تسجيل الدخول. يرجى التحقق من بيانات الاعتماد الخاصة بك.';
      
      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 401) {
          errorMessage = 'بيانات الاعتماد غير صالحة. يرجى التحقق من البريد الإلكتروني وكلمة المرور.';
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'لا يمكن الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.';
      } else if (error.message) {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message;
      }
      
      toast({
        title: 'فشل تسجيل الدخول',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <Stack spacing="8">
        <Stack spacing="6" textAlign="center">
          <Heading size="xl" fontWeight="bold">
            تسجيل الدخول إلى نظام مجالسنا
          </Heading>
          <Text color="gray.500">
            نظام إدارة ترشيحات المرشحين
          </Text>
          <Box 
            p={4} 
            bg="red.50" 
            borderRadius="md" 
            borderWidth="1px" 
            borderColor="red.200"
          >
            <Text color="red.600" fontWeight="medium">
              تنبيه: هذه الصفحة مخصصة لأعضاء اللجنة فقط. إذا كنت مرشحاً، يرجى استخدام رابط "قدم طلب ترشيح" أدناه.
            </Text>
          </Box>
        </Stack>
        <Box
          py={{ base: '0', sm: '8' }}
          px={{ base: '4', sm: '10' }}
          bg={{ base: 'transparent', sm: 'white' }}
          boxShadow={{ base: 'none', sm: 'md' }}
          borderRadius={{ base: 'none', sm: 'xl' }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing="6">
              <Stack spacing="5">
                <FormControl isInvalid={errors.email}>
                  <FormLabel htmlFor="email">البريد الإلكتروني</FormLabel>
                  <Input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'البريد الإلكتروني مطلوب',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'عنوان البريد الإلكتروني غير صالح',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.email && errors.email.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.password}>
                  <FormLabel htmlFor="password">كلمة المرور</FormLabel>
                  <InputGroup>
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', {
                        required: 'كلمة المرور مطلوبة',
                        minLength: {
                          value: 6,
                          message: 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل',
                        },
                      })}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        colorScheme="gray"
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {errors.password && errors.password.message}
                  </FormErrorMessage>
                </FormControl>
              </Stack>

              <HStack justify="space-between">
                <Checkbox defaultChecked>تذكرني</Checkbox>
                <Link as={RouterLink} to="/forgot-password" color="primary.500" fontWeight="semibold" fontSize="sm">
                  نسيت كلمة المرور؟
                </Link>
              </HStack>

              <Stack spacing="6">
                <Button
                  type="submit"
                  colorScheme="blue"
                  variant="solid"
                  isLoading={isLoading}
                  loadingText="جاري تسجيل الدخول..."
                >
                  تسجيل الدخول
                </Button>

                <HStack spacing="1" justify="center">
                  <Text color="gray.500">ليس لديك حساب؟</Text>
                  <Link as={RouterLink} to="/apply" color="primary.500" fontWeight="semibold">
                    قدم طلب ترشيح
                  </Link>
                </HStack>
              </Stack>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Container>
  );
};

export default Login;
